/**
 * BandejaStatCards — Fila de tarjetas-acceso en la parte superior de la
 * Bandeja. Cada tarjeta muestra el conteo de expedientes de su fase y
 * actúa como filtro rápido: al pulsarla, aplica `?fase=<k>` (o limpia
 * el filtro si ya estaba activa).
 *
 * Cards (de izquierda a derecha):
 *  1. Pendientes de contacto (ámbar)
 *  2. Pendientes de visita (azul)
 *  3. Pendientes de preliminar (verde)
 *  4. Pendientes de cierre (morado)
 *  5. Consultas Mapfre <24h (rojo, con sub-badge si hay vencidas)
 *  6. Standby (azul info)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Phone, Calendar, FileText, Lock, Question, Snowflake,
} from "@phosphor-icons/react";

/* Definición de cada tarjeta. `tone` controla el color del chip de icono,
 * `bar` el color del acento inferior cuando está activa, `fase` es el
 * valor que se aplica al filtro de la bandeja. */
const CARDS = [
  {
    k: "pendientes_contacto",
    title: "Pendientes de contacto",
    Icon: Phone,
    tone: "amber",
    countKey: "pendientes_contacto",
  },
  {
    k: "pendientes_visita",
    title: "Pendientes de visita",
    Icon: Calendar,
    tone: "blue",
    countKey: "pendientes_visita",
  },
  {
    k: "pendientes_previo",
    title: "Pendientes de preliminar",
    Icon: FileText,
    tone: "green",
    countKey: "pendientes_previo",
  },
  {
    k: "pendientes_cierre",
    title: "Pendientes de cierre",
    Icon: Lock,
    tone: "purple",
    countKey: "pendientes_cierre",
  },
  {
    k: "consultas_mapfre",
    title: "Consultas Mapfre",
    subtitle: "<24h",
    Icon: Question,
    tone: "red",
    countKey: "consultas_mapfre",
    overdueKey: "consultas_mapfre_vencidas",
  },
  {
    k: "congelados",
    title: "Standby",
    Icon: Snowflake,
    tone: "info",
    countKey: "congelados",
  },
];

const TONE_CLASSES = {
  amber: {
    chip: "bg-status-warning-bg text-status-warning-fg",
    accent: "bg-status-warning-fg",
    activeRing: "ring-status-warning-bd",
  },
  blue: {
    chip: "bg-status-info-bg text-status-info-fg",
    accent: "bg-status-info-fg",
    activeRing: "ring-status-info-bd",
  },
  green: {
    chip: "bg-status-success-bg text-status-success-fg",
    accent: "bg-status-success-fg",
    activeRing: "ring-status-success-bd",
  },
  purple: {
    chip: "bg-accent-purple-bg text-accent-purple-fg",
    accent: "bg-accent-purple-fg",
    activeRing: "ring-accent-purple-bg",
  },
  red: {
    chip: "bg-status-error-bg text-status-error-fg",
    accent: "bg-status-error-fg",
    activeRing: "ring-status-error-bd",
  },
  info: {
    chip: "bg-status-info-bg text-status-info-fg",
    accent: "bg-status-info-fg",
    activeRing: "ring-status-info-bd",
  },
};

export default function BandejaStatCards({ activeFase, onSelect }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/perito-stats")
      .then(({ data }) => { if (!cancelled) setCounts(data?.counts || {}); })
      .catch(() => { if (!cancelled) setCounts({}); });
    return () => { cancelled = true; };
  }, [activeFase]); // recarga cuando se cambia el filtro

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-3"
      data-testid="bandeja-stat-cards"
    >
      {CARDS.map((c) => {
        const active = activeFase === c.k;
        const t = TONE_CLASSES[c.tone];
        const value = counts?.[c.countKey];
        const overdue = c.overdueKey ? counts?.[c.overdueKey] : null;
        return (
          <button
            key={c.k}
            type="button"
            onClick={() => onSelect(active ? "" : c.k)}
            data-testid={`stat-card-${c.k}`}
            aria-pressed={active}
            className={`relative text-left bg-white border border-border rounded-lg overflow-hidden transition-all hover:shadow-sm hover:-translate-y-px ${
              active ? `ring-2 ${t.activeRing} shadow-sm` : ""
            }`}
          >
            <div className="flex items-center gap-3 px-3.5 py-3">
              <span className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${t.chip}`}>
                <c.Icon className="w-4 h-4" weight="regular" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-muted font-medium leading-tight truncate flex items-center gap-1">
                  <span className="truncate">{c.title}</span>
                  {c.subtitle && (
                    <span className="text-[9.5px] uppercase tracking-wider bg-background text-muted border border-border px-1 py-px rounded">
                      {c.subtitle}
                    </span>
                  )}
                </div>
                <div className="text-[22px] font-bold tabular-nums text-foreground leading-tight mt-0.5">
                  {counts === null ? "—" : (value ?? 0)}
                  {overdue ? (
                    <span
                      className="ml-1.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded align-middle bg-status-error-bg text-status-error-fg border border-status-error-bd"
                      title={`${overdue} expediente${overdue === 1 ? "" : "s"} ha superado el SLA de 24h`}
                    >
                      {overdue} vencida{overdue === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            {/* Acento inferior: marcado cuando la card está activa */}
            <div
              className={`h-1 w-full ${active ? t.accent : "bg-transparent"}`}
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );
}
